/* Sticky add-to-cart bar (snippets/sticky-add-to-cart.liquid).
 *
 * Visible once the section's own Add to cart button has scrolled above the
 * viewport, hidden again while the footer is on screen - the reference's
 * behaviour. The button submits the real product form (form attribute), so the
 * theme's Ajax cart handler does the work; this element only mirrors state:
 * the chosen variant's price, title and image, sold-out/unavailable labels and
 * the form's busy state. Survives Theme Editor section reloads. */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.root = this.closest('[data-product-root]');
      this.button = this.querySelector('[data-sticky-button]');
      this.label = this.querySelector('[data-sticky-label]');
      this.dismissButton = this.querySelector('[data-sticky-dismiss]');
      this.mainButton = this.form?.querySelector('button[type="submit"][name="add"]');
      if (!this.form || !this.mainButton || !this.button) return;
      this.hidden = false;
      this.dismissed = false;
      this.onDismiss = () => {
        this.dismissed = true;
        this.removeAttribute('data-visible');
        this.querySelector('[data-sticky-panel]').inert = true;
      };
      this.dismissButton?.addEventListener('click', this.onDismiss);
      this.footer = document.querySelector('.shopify-section-group-footer-group, footer');
      // Position is read on scroll rather than with an IntersectionObserver: a
      // jump or fast fling can carry the button from below the fold to above
      // it without ever intersecting, which an observer never reports.
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.render(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onChange = () => requestAnimationFrame(() => this.sync());
      this.root?.addEventListener('change', this.onChange);
      // the Ajax handler marks the form busy while the cart request runs
      this.busyObserver = new MutationObserver(() => this.sync());
      this.busyObserver.observe(this.form, { attributes: true, attributeFilter: ['aria-busy'] });
      this.sync();
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.busyObserver?.disconnect();
      this.root?.removeEventListener('change', this.onChange);
      this.dismissButton?.removeEventListener('click', this.onDismiss);
    }

    render() {
      const pastButton = this.mainButton.getBoundingClientRect().bottom < 0;
      const footerVisible = Boolean(this.footer) && this.footer.getBoundingClientRect().top < window.innerHeight;
      const show = !this.dismissed && pastButton && !footerVisible;
      this.toggleAttribute('data-visible', show);
      // keep the off-screen bar out of the tab order and the accessibility tree
      this.querySelector('[data-sticky-panel]').inert = !show;
    }

    sync() {
      const picker = this.root?.querySelector('variant-picker');
      const idInput = this.form.querySelector('[data-variant-id-input]');
      const id = idInput?.value;
      const variant = picker?.variants?.find(v => String(v.id) === String(id));
      const busy = this.form.hasAttribute('aria-busy');
      const available = Boolean(id) && !idInput.disabled;
      this.button.disabled = busy || !available;
      this.label.textContent = available ? this.dataset.addLabel
        : (id ? this.dataset.soldOutLabel : this.dataset.unavailableLabel);
      if (!variant) return;
      const presentation = picker.presentations?.[String(variant.id)];
      const price = this.querySelector('[data-sticky-price]');
      if (presentation?.price && price) price.innerHTML = presentation.price;
      const title = this.querySelector('[data-sticky-variant]');
      if (title && variant.title && variant.title !== 'Default Title') title.textContent = variant.title;
      const src = variant.featured_media?.preview_image?.src;
      const img = this.querySelector('.sticky-atc__image');
      if (src && img) {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '240');
        img.removeAttribute('srcset');
        img.src = url.toString();
      }
    }
  });
}
